/**
 * System capabilities — feature-flag detection (v6.6.2, task #74)
 *
 * Leser hva backend støtter/har aktivert fra ett bulk-endepunkt:
 *   GET /api/system/capabilities (public — ingen auth)
 *
 * Erstatter tidligere ad-hoc "probe et endepunkt"-mønster (v6.6.1) med
 * autoritativ liste av feature-flagg. Én rundetur ved app-oppstart cacher
 * resultatet for hele sesjonen.
 *
 * ADD FEATURES ANYTIME:
 *   1. Detekter i backend/email-service/routes/system-capabilities.js
 *   2. Legg feltet til i interface `SystemCapabilities` under
 *   3. Bruk `caps.<field>` i komponent-gate
 *
 * FAIL-SAFE: Hvis endepunktet feiler eller er utilgjengelig, returnerer vi
 * `null` (unknown). Komponenter behandler `null` og `false` likt — features
 * skjules. Bedre å vise for lite enn feil UI.
 */
import { useEffect, useState } from 'react';

export interface SystemCapabilities {
  // Fase 1 + 2 + 2.5 — kompositt-vurdering
  /** assessment_rubrics-tabell finnes (Fase 2 T2.1) — Vurderingsmatrise-menypunkt */
  assessment_rubrics: boolean;
  /** assessment_plans + assessment_components (Fase 1 T1.1-T1.5) — Vurderingsoppsett-tab */
  composite_assessment: boolean;
  /** final_assessments (Fase 2.5 T2.5.4) — Finaliser-modal + Sensur-oversikt-tab */
  composite_finalize: boolean;

  // Fase 1 K17 — KI-token-fakturering
  /** member_ai_quotas-tabell + backend-cron (Fase 1 T1.7-T1.12) */
  ai_token_quotas: boolean;

  // Fase 3 (I2 forberedelser) — KI-preview
  /** assessment_submission_ai_reviews + I2_KI_PREVIEW_ENABLED env */
  ai_preview: boolean;

  // v6.6.1 — Kandidat-godkjenningsflyt
  /** course_candidates.approval_status finnes */
  candidate_approval_flow: boolean;
  /** course_instances.approval_sla_days finnes */
  approval_sla: boolean;

  // v6.0/v6.1 — DPA-infrastruktur
  /** dpa_templates-tabell finnes */
  dpa_v6: boolean;
  /** signature_completion_groups-tabell finnes */
  multi_signature: boolean;

  // v6.4 Blokk 7 — Underdatabehandlere
  /** sub_processors-tabell finnes — admin-UI (/settings/subprocessors) */
  subprocessor_admin: boolean;
  /** Minst én aktiv underleverandør er publisert — public /underdatabehandlere-side */
  subprocessors_published: boolean;

  // v5.16 — LMS-integrasjoner
  /** mentorkit_integrations-tabell finnes */
  mentorkit: boolean;

  // Andre features
  vitnemalsportalen: boolean;
  karrierepasset: boolean;
  career_history: boolean;
  interest_categories: boolean;

  // Meta
  generated_at: string;
  environment: 'test' | 'prod';
}

let cache: SystemCapabilities | null = null;
let inFlight: Promise<SystemCapabilities | null> | null = null;

export async function getSystemCapabilities(): Promise<SystemCapabilities | null> {
  if (cache) return cache;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const res = await fetch('/api/system/capabilities');
      if (!res.ok) {
        console.warn('[SystemCapabilities] endpoint returned', res.status);
        return null;
      }
      const data = await res.json();
      if (!data.success || !data.capabilities) {
        console.warn('[SystemCapabilities] endpoint returned invalid payload');
        return null;
      }
      return data.capabilities as SystemCapabilities;
    } catch (err) {
      console.warn('[SystemCapabilities] fetch failed:', err);
      return null;
    }
  })();

  cache = await inFlight;
  inFlight = null;
  return cache;
}

/**
 * Hovedhook — returnerer null under første fetch og ved feil. Etter det: hele
 * capabilities-objektet. Bruksmønster:
 *
 *   const caps = useSystemCapabilities();
 *   if (!caps?.composite_assessment) return null;
 *
 * Behandle null (unknown) og false (deaktivert) likt — begge gjemmer feature.
 */
export function useSystemCapabilities(): SystemCapabilities | null {
  const [caps, setCaps] = useState<SystemCapabilities | null>(cache);
  useEffect(() => {
    if (!cache) {
      getSystemCapabilities().then(setCaps);
    }
  }, []);
  return caps;
}

/**
 * BAKOVERKOMPATIBEL: eldre `compositeAssessment`-felt fra v6.6.1-versjonen av
 * denne modulen. Nye komponenter bør bruke `caps.composite_assessment` direkte.
 *
 * @deprecated bruk `useSystemCapabilities()` og les feltet du trenger
 */
export function useCompositeAssessment(): { compositeAssessment: boolean } | null {
  const caps = useSystemCapabilities();
  if (!caps) return null;
  return { compositeAssessment: caps.composite_assessment };
}
